import React from "react";
import useAuthStore from "store/authStore";
import { useLocation } from "react-router-dom";
import { toLoginUrl } from "utils/intendedUrl";

const useAllowedBookActions = ({
	isFree,
	in_library,
	in_picks,
	is_purchased,
	sellable,
	subscribable,
	hasSubscription,
	freeBooksQuota,
}) => {
	const { loggedIn: isLoggedIn } = useAuthStore();

	const location = useLocation();
	const loginUrl = toLoginUrl(location);

	const quota = freeBooksQuota?.data?.data ?? null;
	const unlockedUnlimited = Boolean(quota?.unlocked_unlimited);
	const remainingFreeBooks = quota?.remaining_free_books;
	const maxFreeBooks = quota?.max_free_books;

	/**
	 * add a subscribable book to picks
	 */
	const addToPicks = React.useMemo(() => {
		let message = "";
		let action = undefined;

		if (!isLoggedIn) {
			message = "You need to login to continue with this action";
			action = { label: "Login", link: loginUrl };
			return {
				possible: false,
				message,
				action,
			};
		}

		if (in_picks) {
			message = "You have already added this book to picks";
			return { possible: false, message, action };
		}

		// if (in_picks && isFree) {
		// 	message = "You already have access to this book";
		// 	return { possible: false, message, action };
		// }

		if (is_purchased) {
			message = "You do not need to add this book to picks because you have already purchased it";
			return { possible: false, message, action };
		}

		if (isFree) {
			if (unlockedUnlimited) {
				return { possible: true, message: undefined, action };
			}

			if (quota === null) {
				return { possible: true, message: undefined, action };
			}

			if (remainingFreeBooks > 0) {
				return { possible: true, message: undefined, action };
			}

			message =
				maxFreeBooks != null
					? `You have reached the maximum of ${maxFreeBooks} free books. Purchase or subscribe to unlock unlimited free books.`
					: "You have reached your free book limit. Purchase or subscribe to unlock unlimited free books.";
			action = { label: "Subscribe", link: "/subscriptions" };

			return {
				possible: false,
				message,
				action,
			};
		}

		if (!hasSubscription) {
			if (!subscribable) {
				message =
					"This book is not available through subscription and can not be added to your picks. To access it, please purchase it directly.";
			} else {
				message = "You need an active subscription to add this book to picks.";
				action = { label: "Subscribe", link: "/subscriptions" };
			}

			return {
				possible: false,
				message,
				action,
			};
		}

		if (!subscribable && !isFree) {
			message = "You can't add this book to picks because it is not available for subscription";
			return { possible: false, message, action };
		}

		return { possible: true, message: undefined, action };
	}, [
		isLoggedIn,
		in_picks,
		is_purchased,
		isFree,
		hasSubscription,
		subscribable,
		loginUrl,
		unlockedUnlimited,
		quota,
		remainingFreeBooks,
		maxFreeBooks,
	]);

	/**
	 * Buy a single book item
	 */
	const purchase = React.useMemo(() => {
		let message = "";

		if (!isLoggedIn) {
			message = "You need to login to continue with this action";
			return {
				possible: false,
				message,
				action: { label: "Login", link: loginUrl },
			};
		}

		if (isFree) {
			message = "You don't need to purchase this book because it is free";
			return { possible: false, message, action: undefined };
		}

		if (!sellable) {
			message = isFree
				? "You don't need to purchase this book because it is free"
				: "This book is not available for purchase. Try adding it to picks if you have an active subscription";
			return {
				possible: false,
				message,
				action: undefined,
			};
		}

		return { possible: true, message: undefined, action: undefined };
	}, [isFree, sellable, isLoggedIn, loginUrl]);

	/**
	 * add a book to cart
	 */
	const addToCart = React.useMemo(() => {
		let message = "";

		if (!isLoggedIn) {
			message = "You need to login to continue with this action";
			return {
				possible: false,
				message,
				action: { label: "Login", link: loginUrl },
			};
		}

		if (isFree) {
			message = "You don't need to add this book to cart because it is free";
			return { possible: false, message, action: undefined };
		}

		if (!sellable) {
			message = isFree
				? "You don't need to purchase this book because it is free"
				: "This book is not available for purchase. Try adding it to picks if you have an active subscription";
			return { possible: false, message, action: undefined };
		}

		return { possible: true, message: undefined, action: undefined };
	}, [isLoggedIn, isFree, sellable, loginUrl]);

	/**
	 * remove a book from picks
	 */
	const removeFromPicks = React.useMemo(() => {
		let message = "";
		if (!isLoggedIn) {
			message = "You need to login to continue with this action";
			return {
				possible: false,
				message,
				action: { label: "Login", link: loginUrl },
			};
		}

		if (in_picks) {
			if (!isFree) {
				message = "Only free books can be removed from picks";
				return { possible: false, message, action: undefined };
			} else {
				return { possible: true, message: undefined, action: undefined };
			}
		}

		return { possible: false, message: undefined, action: undefined };
	}, [in_picks, isFree, isLoggedIn, loginUrl]);

	return {
		addToPicks,
		purchase,
		addToCart,
		removeFromPicks,
	};
};

export default useAllowedBookActions;
